import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Heart, ArrowRight, ArrowsClockwise, Info, CheckCircle, Warning,
  PauseCircle, PlugsConnected, Hourglass, ShieldWarning, Handshake,
  EnvelopeSimpleOpen, Prohibit, XCircle, ArrowUUpLeft,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatLocal } from "@/lib/timezone";
import LeadStatusChip from "@/components/LeadStatusChip";

// Continuous-nurture Drip Health (2026-10-28). Every live sales lead is in
// exactly one bucket of backend `nurture_policy` — the same classification
// the System Health check, the daily marketing email and the daily
// reconciliation sweep use. The headline should normally read 0.

const ACTION_TILES = [
  { key: "eligible_not_enrolled", icon: PlugsConnected },
  { key: "paused_stranded",       icon: PauseCircle },
  { key: "all_errored",           icon: XCircle },
  { key: "lead_paused",           icon: Prohibit },
  { key: "quarantined",           icon: ShieldWarning },
  { key: "sales_overdue",         icon: Hourglass },
];
const EXPECTED_TILES = [
  { key: "active_nurture", icon: EnvelopeSimpleOpen },
  { key: "sales_owned",    icon: Handshake },
  { key: "unsubscribed",   icon: Prohibit },
  { key: "invalid",        icon: Warning },
];
const SALES_BUCKETS = new Set(["sales_owned", "sales_overdue"]);
const ENROLMENT_BUCKETS = new Set(["paused_stranded", "all_errored", "active_nurture"]);

function Tile({ tile, info, active, onClick, actionable }) {
  const Icon = tile.icon;
  const n = info?.count;
  const alarm = actionable && n > 0;
  return (
    <button
      onClick={onClick}
      data-testid={`drip-health-tile-${tile.key}`}
      className={`text-left border p-3 transition-all ${active ? "border-[#0055FF] bg-[#EFF6FF] shadow-sm" : "border-[#E5E5E5] bg-white hover:border-[#0055FF]"}`}
    >
      <div className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider ${alarm ? "text-[#991B1B]" : "text-[#525252]"}`}>
        <Icon size={11} weight="bold" /> {info?.label || tile.key}
      </div>
      <div className={`text-2xl font-black tracking-tight mt-1.5 ${alarm ? "text-[#B91C1C]" : "text-[#0A0A0A]"}`}>
        {n ?? "—"}
      </div>
    </button>
  );
}

export default function DripHealthPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bucket, setBucket] = useState("eligible_not_enrolled");
  const [returning, setReturning] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/crm/drip-health?bucket=${bucket}&limit=200`);
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Drip Health could not be loaded. Please refresh.");
    }
    setLoading(false);
  }, [bucket]);

  useEffect(() => { load(); }, [load]);

  const returnToNurture = async (it) => {
    if (!window.confirm(
      `Return ${it.company_name || "this lead"} to long-term nurture?\n\n` +
      "Use this when Sales is no longer actively working the lead. Automated " +
      "nurture resumes in the normal send window; the sales status is not reopened.")) return;
    setReturning(it.lead_id);
    try {
      const r = await api.post(`/crm/leads/${it.lead_id}/return-to-nurture`, {});
      toast.success(r.data?.message || "Returned to nurture");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not return the lead to nurture. Nothing was changed.");
    }
    setReturning(null);
  };

  const head = data?.headline;
  const gap = head?.marketable_without_nurture;
  const info = { ...(data?.action_needed || {}), ...(data?.expected || {}) };
  const current = info[bucket];
  const items = data?.items || [];

  return (
    <div className="p-6 max-w-screen-2xl mx-auto space-y-6" data-testid="drip-health-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-black tracking-tight text-[#0A0A0A] flex items-center gap-2">
            <Heart size={28} weight="bold" /> Drip Health
          </h1>
          <p className="text-xs text-[#525252] mt-1.5 max-w-3xl leading-snug">
            Every live sales lead must be in active nurture unless they unsubscribed, the
            address is invalid, or Sales is genuinely working them. Closed Lost and finished
            workflows are not reasons to stop nurturing.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-[#E5E5E5] text-[#525252] text-[10px] font-bold uppercase tracking-wider hover:border-[#0055FF] hover:text-[#0055FF] disabled:opacity-50"
          data-testid="drip-health-refresh"
        >
          <ArrowsClockwise size={11} weight="bold" />
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {error && (
        <div className="border border-[#FECACA] bg-[#FEF2F2] p-4 text-sm text-[#991B1B]" data-testid="drip-health-error">
          {error}
        </div>
      )}

      {head && (
        <div
          className={`border p-5 flex items-center gap-4 ${gap === 0 ? "border-[#BBF7D0] bg-[#F0FDF4]" : "border-[#FECACA] bg-[#FEF2F2]"}`}
          data-testid="drip-health-headline"
        >
          {gap === 0
            ? <CheckCircle size={36} weight="fill" className="text-[#15803D] shrink-0" />
            : <Warning size={36} weight="fill" className="text-[#B91C1C] shrink-0" />}
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-[#525252]">
              {head.question}
            </div>
            <div className="text-4xl font-black tracking-tight text-[#0A0A0A]" data-testid="drip-health-gap">
              {gap}
            </div>
            <div className="text-xs text-[#525252]">
              {gap === 0
                ? `All ${head.total_live_leads} live leads are nurtured, with Sales, or permanently excluded.`
                : "Should be 0. The daily sweep enrols eligible leads automatically; paused, errored and quarantined leads need a decision."}
            </div>
          </div>
        </div>
      )}

      <section>
        <h2 className="text-[11px] font-bold uppercase tracking-wider text-[#991B1B] mb-2">Action needed</h2>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {ACTION_TILES.map(t => (
            <Tile key={t.key} tile={t} info={info[t.key]} actionable
                  active={bucket === t.key} onClick={() => setBucket(t.key)} />
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-[11px] font-bold uppercase tracking-wider text-[#525252] mb-2">Expected / excluded</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {EXPECTED_TILES.map(t => (
            <Tile key={t.key} tile={t} info={info[t.key]}
                  active={bucket === t.key} onClick={() => setBucket(t.key)} />
          ))}
        </div>
      </section>

      <div className="border border-[#E5E5E5] bg-white">
        <div className="px-4 py-3 border-b border-[#E5E5E5] bg-[#FAFAFA] flex items-center gap-2">
          <Info size={14} className="text-[#525252] shrink-0" />
          <span className="text-xs text-[#525252]" data-testid="drip-health-bucket-description">
            <strong>{current?.label}</strong>{current ? " — " : ""}{current?.description}
          </span>
        </div>

        {loading && (
          <div className="p-10 flex justify-center" data-testid="drip-health-loading">
            <div className="w-8 h-8 border-2 border-[#0055FF] border-t-transparent animate-spin" />
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="p-10 text-center" data-testid="drip-health-empty">
            <CheckCircle size={28} className="text-[#A3A3A3] mx-auto mb-2" />
            <p className="text-sm font-bold text-[#525252]">No leads in "{current?.label}".</p>
          </div>
        )}

        {!loading && items.length > 0 && (
          <table className="w-full text-sm" data-testid="drip-health-table">
            <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
              <tr className="text-[10px] font-bold uppercase tracking-wider text-[#525252]">
                <th className="px-4 py-2 text-left">Lead</th>
                <th className="px-4 py-2 text-left">Status</th>
                {ENROLMENT_BUCKETS.has(bucket) && <th className="px-4 py-2 text-left">Enrolment</th>}
                {bucket === "lead_paused" && <th className="px-4 py-2 text-left">Pause reason</th>}
                {SALES_BUCKETS.has(bucket) && <th className="px-4 py-2 text-left">With Sales since</th>}
                <th className="px-4 py-2 text-left">Last action</th>
                <th className="px-4 py-2 text-right" />
              </tr>
            </thead>
            <tbody>
              {items.map(it => (
                <tr key={it.lead_id} className="border-b border-[#F5F5F5] hover:bg-[#FAFAFA]">
                  <td className="px-4 py-3">
                    <div className="font-bold text-[#0A0A0A]">{it.company_name || "—"}</div>
                    <div className="text-[10px] text-[#A3A3A3]">{it.contact_person} · {it.contact_email}</div>
                  </td>
                  <td className="px-4 py-3"><LeadStatusChip status={it.lead_status} /></td>
                  {ENROLMENT_BUCKETS.has(bucket) && (
                    <td className="px-4 py-3 text-xs text-[#525252]">
                      {(it.enrolments || []).map((e, i) => (
                        <div key={i}>
                          {e.workflow} · <span className="font-bold">{e.status}</span>
                          {e.pause_reason ? ` (${e.pause_reason})` : ""}
                          {e.status === "active" && e.next_action_at ? ` · next ${formatLocal(e.next_action_at)}` : ""}
                        </div>
                      ))}
                    </td>
                  )}
                  {bucket === "lead_paused" && (
                    <td className="px-4 py-3 text-xs text-[#525252]">
                      {it.pause_reason || <span className="text-[#A3A3A3] italic">no reason recorded</span>}
                    </td>
                  )}
                  {SALES_BUCKETS.has(bucket) && (
                    <td className="px-4 py-3 text-xs text-[#525252]">
                      {it.sales_entered_at ? formatLocal(it.sales_entered_at) : "—"}
                    </td>
                  )}
                  <td className="px-4 py-3 text-xs text-[#A3A3A3]">
                    {it.last_action_date ? formatLocal(it.last_action_date) : "—"}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {SALES_BUCKETS.has(bucket) && (
                      <button
                        onClick={() => returnToNurture(it)}
                        disabled={returning === it.lead_id}
                        className="inline-flex items-center gap-1 px-2 py-1 mr-2 text-[10px] font-bold uppercase tracking-wider border border-[#E5E5E5] text-[#525252] hover:border-[#15803D] hover:text-[#15803D] disabled:opacity-50"
                        data-testid={`drip-health-return-${it.lead_id}`}
                      >
                        <ArrowUUpLeft size={10} weight="bold" />
                        {returning === it.lead_id ? "Returning…" : "Return to nurture"}
                      </button>
                    )}
                    <Link
                      to={`/crm/leads/${it.lead_id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider border border-[#E5E5E5] text-[#525252] hover:border-[#0055FF] hover:text-[#0055FF]"
                      data-testid={`drip-health-open-${it.lead_id}`}
                    >
                      Open <ArrowRight size={10} weight="bold" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
